/**
 * useMilestoneTimeline — FE-WIRING-1A
 * ====================================
 *
 * Server-state hook for the canonical patient-facing milestone timeline.
 *
 * AUTHORITY:
 *   - Sole authority: `treatment_cases → milestone_instances` (COM-1D.D).
 *   - Reads via `GET /api/cases/person/{patient_id}` (case resolution)
 *     and `GET /api/milestone-instances/case/{case_id}` (canonical
 *     milestone timeline).
 *   - Server response is consumed verbatim. No FE shadow/fallback state.
 *
 * STRICT BOUNDARIES (FE-CONTRACT-LOCK):
 *   - R-1: this hook does NOT create a parallel workflow store. The last
 *     server payload is held in component state for rendering only.
 *   - R-6: no fallback / autoheal / synthetic-progress chains. Errors are
 *     surfaced to the caller; UI must render an error/empty state.
 *   - R-11: TimelineContext is HTTP-call-free; this hook lives OUTSIDE
 *     TimelineContext and never imports it.
 *   - R-13: patient-facing — does NOT touch `/api/milestone-tasks/*`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

/**
 * @param {object} opts
 * @param {string|null|undefined} opts.patientId  Authenticated patient id (used to
 *                                                resolve the canonical case when no
 *                                                caseId is supplied).
 * @param {string|null|undefined} opts.caseId     Optional explicit treatment_cases.case_id.
 *                                                When supplied, no person→case resolution
 *                                                is performed.
 */
export function useMilestoneTimeline({ patientId, caseId: explicitCaseId } = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [caseId, setCaseId] = useState(explicitCaseId || null);
  const [milestones, setMilestones] = useState([]);

  // Prevent stale-response races when patientId/caseId changes.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);

    try {
      // 1. Resolve canonical case_id (when not explicitly provided).
      let resolvedCaseId = explicitCaseId || null;
      if (!resolvedCaseId) {
        if (!patientId) {
          if (seq === requestSeq.current) {
            setCaseId(null);
            setMilestones([]);
            setLoading(false);
          }
          return;
        }
        const casesRes = await api.get(`/cases/person/${patientId}`);
        const cases = (casesRes && casesRes.data && casesRes.data.cases) || [];
        // Pick the most recent active case. Backend already sorts created_at DESC.
        const active = cases.find(
          (c) => c && c.status && c.status !== 'cancelled' && c.status !== 'completed'
        );
        resolvedCaseId = (active || cases[0] || {}).case_id || null;
      }

      if (seq !== requestSeq.current) return;
      setCaseId(resolvedCaseId);

      if (!resolvedCaseId) {
        if (seq === requestSeq.current) {
          setMilestones([]);
          setLoading(false);
        }
        return;
      }

      // 2. Fetch canonical milestone timeline for the case.
      const res = await api.get(`/milestone-instances/case/${resolvedCaseId}`);
      const payload = res && res.data ? res.data : {};

      if (seq !== requestSeq.current) return;

      if (payload.success !== true) {
        throw new Error('canonical_milestone_read_failed');
      }
      setMilestones(Array.isArray(payload.milestones) ? payload.milestones : []);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      // No fallback / shadow / autoheal (R-6). Surface error verbatim.
      setError(e);
      setMilestones([]);
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [patientId, explicitCaseId]);

  useEffect(() => {
    load();
  }, [load]);

  return { loading, error, caseId, milestones, refetch: load };
}

/**
 * Display-only status map (R-3 — aliases are additive only).
 * Maps canonical enum → user-facing label + tailwind color class.
 * Canonical enum (HYGIENE-2 lock): pending | scheduled | in_progress | achieved | skipped | blocked.
 */
export const MILESTONE_STATUS_DISPLAY = {
  pending: { label: 'Pending', badgeClass: 'bg-slate-100 text-slate-600 border-slate-200' },
  scheduled: { label: 'Scheduled', badgeClass: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  in_progress: { label: 'In Progress', badgeClass: 'bg-blue-100 text-blue-700 border-blue-200' },
  achieved: { label: 'Completed', badgeClass: 'bg-green-100 text-green-700 border-green-200' },
  skipped: { label: 'Skipped', badgeClass: 'bg-slate-100 text-slate-500 border-slate-200' },
  blocked: { label: 'Blocked', badgeClass: 'bg-red-100 text-red-700 border-red-200' },
};

/**
 * Compute completion percentage from canonical milestones (achieved only).
 */
export function computeMilestoneProgress(milestones) {
  if (!Array.isArray(milestones) || milestones.length === 0) return 0;
  const done = milestones.filter((m) => m && m.status === 'achieved').length;
  return Math.round((done / milestones.length) * 100);
}

/**
 * Pick the "current" milestone (first non-achieved/skipped, otherwise the last).
 */
export function pickCurrentMilestone(milestones) {
  if (!Array.isArray(milestones) || milestones.length === 0) return null;
  const active = milestones.find(
    (m) => m && m.status !== 'achieved' && m.status !== 'skipped'
  );
  return active || milestones[milestones.length - 1];
}

export default useMilestoneTimeline;
